import { pipe, prop } from 'remeda';

import currentFileManager from '@core/app/svgedit/currentFileManager';
import selectionManager from '@core/app/svgedit/selection';
import { updateRecentFiles } from '@core/helpers/file/recentFiles';
import { getOS } from '@core/helpers/getOS';
import i18n from '@core/helpers/i18n';
import isWeb from '@core/helpers/is-web';
import { getSVGAsync } from '@core/helpers/svg-editor-helper';
import { convertVariableText } from '@core/helpers/variableText';
import dialog from '@core/implementations/dialog';
import fs from '@core/implementations/fileSystem';
import type ISVGCanvas from '@core/interfaces/ISVGCanvas';

import { generateBeamBuffer } from '../utils/beam';
import { getDefaultFileName, switchSymbolWrapper } from '../utils/common';
import { checkNounProjectElements, removeNPElementsWrapper } from '../utils/nounProject';

import { saveToCloud } from './cloud';

let svgCanvas: ISVGCanvas;

getSVGAsync(({ Canvas }) => {
  svgCanvas = Canvas;
});

export const saveAsFile = async (): Promise<boolean> => {
  selectionManager.clearSelection();
  svgCanvas.removeUnusedDefs();

  const defaultFileName = getDefaultFileName();
  const langFile = i18n.lang.topmenu.file;
  const getContent = async () =>
    pipe(
      await generateBeamBuffer(),
      (buffer) => Uint8Array.from(buffer),
      prop('buffer'),
      (arrayBuffer) => new Blob([arrayBuffer]),
    );

  const newFilePath = await dialog.writeFileDialog(
    getContent,
    langFile.save_scene,
    getOS() === 'Linux' ? `${defaultFileName}.beam` : defaultFileName,
    [
      { extensions: ['beam'], name: getOS() === 'MacOS' ? `${langFile.scene_files} (*.beam)` : langFile.scene_files },
      { extensions: ['*'], name: i18n.lang.topmenu.file.all_files },
    ],
  );

  if (newFilePath) {
    currentFileManager.setLocalFile(newFilePath);
    updateRecentFiles(newFilePath);
    currentFileManager.setHasUnsavedChanges(false, false);

    return true;
  }

  if (isWeb()) {
    currentFileManager.setHasUnsavedChanges(false, false);

    return true;
  }

  return false;
};

export const saveFile = async (): Promise<boolean> => {
  const path = currentFileManager.getPath();

  if (!path) {
    return await saveAsFile();
  }

  selectionManager.clearSelection();
  svgCanvas.removeUnusedDefs();

  if (currentFileManager.isCloudFile) {
    return saveToCloud(path);
  }

  if (path.endsWith('.bvg')) {
    if (!(await checkNounProjectElements())) {
      return false;
    }

    const output = await removeNPElementsWrapper(() =>
      switchSymbolWrapper(async () => {
        const revert = await convertVariableText();

        try {
          return svgCanvas.getSvgString();
        } finally {
          revert?.();
        }
      }),
    );

    await fs.writeFile(path, output);
    currentFileManager.setHasUnsavedChanges(false, false);

    return true;
  }

  if (path.endsWith('.beam')) {
    const buffer = await generateBeamBuffer();

    await fs.writeFile(path, buffer);
    currentFileManager.setHasUnsavedChanges(false, false);

    return true;
  }

  return false;
};
